/**
 * KunstPfote – Upload-Modal mit Foto-Prüfung (Markup/CSS: snippets/kp-upload-modal.liquid)
 *
 * - Fängt Klicks/Drops auf den bestehenden Upload-Flächen ab (.pf-dropzone = Produktseite,
 *   .photo-upload__dropzone = Startseite) und öffnet stattdessen dieses Modal.
 * - Lädt Fotos auf den Upload-Server, pflegt localStorage (kunstpfote_session, kunstpfote_uploads)
 *   und feuert dieselben Events wie bisher (kunstpfote:photos-uploaded / photo-removed), damit
 *   Produktkarte, Startseiten-Schritte, „Meine Fotos“ und Floating-Button sich wie gewohnt aktualisieren.
 * - Fragt das Prüfergebnis ab: GET {uploadBase}/check/{sessionId} (n8n „KunstPfote – Fotos prüfen“).
 *   Fotos mit KI/Grafik, Screenshot, Person oder ohne Tier verwendet der Server nicht (nur markiert).
 * - Fragt den Tiernamen ab (KunstPfotePetName), solange das Modal offen ist, bleibt der separate
 *   Namensdialog aus (pet-name.js prüft window.KunstPfoteUpload.isOpen()).
 *
 * API: window.KunstPfoteUpload.open({ files?, source? }), .close(), .isOpen()
 */
(() => {
  const SESSION_KEY = 'kunstpfote_session';
  const DATA_KEY = 'kunstpfote_uploads';
  const MAX_FILES = 4;
  const MAX_SIZE = 10 * 1024 * 1024;
  const TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const DROPZONES = '.pf-dropzone, .photo-upload__dropzone';
  const POLL_MS = 1500;
  const POLL_MAX = 45; // ≈ 70 s
  const SMALL_PX = 500;
  const MIN_GOOD = 2; // so viele gute/geeignete Fotos braucht es mindestens (Server: MIN_GOOD_PHOTOS)

  const ICON_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  const ICON_PLUS = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';

  const STATE_TEXT = {
    uploading: 'Wird hochgeladen …',
    error: 'Upload fehlgeschlagen',
    pending: 'Wird geprüft …',
    good: 'Gut geeignet',
    ok: 'Geeignet',
    poor: 'Eher ungeeignet',
    rejected: 'Wird nicht verwendet',
    skipped: 'Lasse ich weg',
  };

  function readData() {
    try {
      const d = JSON.parse(localStorage.getItem(DATA_KEY) || 'null');
      return d && d.sessionId ? d : null;
    } catch {
      return null;
    }
  }

  function writeData(d) {
    try { localStorage.setItem(DATA_KEY, JSON.stringify(d)); } catch {}
  }

  function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  class KpUploadModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.grid = this.querySelector('[data-kpu-grid]');
      this.drop = this.querySelector('[data-kpu-drop]');
      this.input = this.querySelector('[data-kpu-input]');
      this.summary = this.querySelector('[data-kpu-summary]');
      this.errorEl = this.querySelector('[data-kpu-error]');
      this.nameInput = this.querySelector('[data-kpu-name]');
      this.primary = this.querySelector('[data-kpu-primary]');
      this.addBtn = this.querySelector('[data-kpu-add]');
      this.endpoint = this.dataset.uploadEndpoint;
      this.uploadBase = this.endpoint.replace(/\/upload$/, '');

      this.entries = [];
      this.check = { status: 'none', photos: {} };
      this.checkEnabled = true;
      this.queue = Promise.resolve();
      this.pollTimer = null;
      this.polls = 0;
      this.source = null;

      // Bedienung im Modal
      this.querySelector('[data-kpu-close]').addEventListener('click', () => this.close());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
      this.dialog.addEventListener('close', () => this.#onClosed());
      this.drop.addEventListener('click', () => this.input.click());
      this.drop.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.input.click(); }
      });
      this.addBtn.addEventListener('click', () => this.input.click());
      this.input.addEventListener('change', () => {
        this.addFiles(this.input.files);
        this.input.value = '';
      });
      this.primary.addEventListener('click', () => this.#onPrimary());
      // Name übernehmen, sobald das Feld verlassen wird (PDP-Häkchen „Name aufs Bild“ wird damit vorbelegt)
      this.nameInput.addEventListener('change', () => {
        const data = readData();
        const value = this.nameInput.value.trim();
        if (data && window.KunstPfotePetName && value !== (data.petName || '')) window.KunstPfotePetName.set(value);
      });
      this.grid.addEventListener('click', (e) => {
        const del = e.target.closest('[data-kpu-del]');
        if (del) this.removeEntry(del.dataset.kpuDel);
        if (e.target.closest('[data-kpu-add-tile]')) this.input.click();
      });

      // Drag & Drop aufs ganze Modal
      this.dialog.addEventListener('dragover', (e) => {
        e.preventDefault();
        this.drop.classList.add('kpu__drop--active');
      });
      this.dialog.addEventListener('dragleave', (e) => {
        if (!this.dialog.contains(e.relatedTarget)) this.drop.classList.remove('kpu__drop--active');
      });
      this.dialog.addEventListener('drop', (e) => {
        e.preventDefault();
        this.drop.classList.remove('kpu__drop--active');
        if (e.dataTransfer?.files?.length) this.addFiles(e.dataTransfer.files);
      });

      this.#interceptDropzones();
    }

    // ── Öffnen / Schließen ───────────────────────────────────────────────────

    isOpen() {
      return this.dialog.open;
    }

    open({ files = null, source = null } = {}) {
      this.source = source;
      this.#syncFromStorage();
      this.errorEl.hidden = true;
      this.nameInput.value = window.KunstPfotePetName?.get?.() || '';
      if (!this.dialog.open) {
        this.dialog.showModal();
        this.prevOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      }
      this.render();
      if (files?.length) this.addFiles(files);
      else if (this.entries.length) this.#refreshCheck();
      else this.drop.focus({ preventScroll: true });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    #onClosed() {
      document.documentElement.style.overflow = this.prevOverflow || '';
      clearTimeout(this.pollTimer);
      this.#saveName();
    }

    #saveName() {
      const data = readData();
      if (!data || !window.KunstPfotePetName) return;
      const value = this.nameInput.value.trim();
      // Nur speichern, wenn etwas eingegeben wurde oder noch nie gefragt wurde ('' = „ohne Namen“)
      if (value || !('petName' in data)) window.KunstPfotePetName.set(value);
    }

    // ── Bestehende Upload-Flächen abfangen ───────────────────────────────────

    #interceptDropzones() {
      const zoneOf = (e) => e.target.closest?.(DROPZONES);

      document.addEventListener('click', (e) => {
        const zone = zoneOf(e);
        if (!zone || this.contains(zone)) return;
        e.preventDefault();
        e.stopPropagation(); // eigener Klick-Handler der Fläche (Dateiauswahl) soll nicht laufen
        this.open({ source: zone });
      }, true);

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const zone = zoneOf(e);
        if (!zone || e.target !== zone) return;
        e.preventDefault();
        e.stopPropagation();
        this.open({ source: zone });
      }, true);

      document.addEventListener('drop', (e) => {
        const zone = zoneOf(e);
        if (!zone || !e.dataTransfer?.files?.length) return;
        e.preventDefault();
        e.stopPropagation();
        zone.classList.remove('pf-dropzone--active', 'photo-upload__dropzone--active');
        this.open({ source: zone, files: e.dataTransfer.files });
      }, true);
    }

    // ── Zustand aus localStorage ─────────────────────────────────────────────

    #syncFromStorage() {
      const data = readData();
      const stored = data?.originals || [];
      // Laufende Uploads behalten, fertige an localStorage ausrichten (z. B. in „Meine Fotos“ gelöscht)
      const keep = this.entries.filter((e) => e.state !== 'done' || stored.includes(e.filename));
      for (const filename of stored) {
        if (!keep.some((e) => e.filename === filename)) {
          keep.push({ id: crypto.randomUUID(), filename, state: 'done', src: this.#url(data.sessionId, filename) });
        }
      }
      this.entries = keep;
    }

    #url(sessionId, filename) {
      return `${this.uploadBase}/image/${sessionId}/originals/${filename}`;
    }

    // ── Hochladen ────────────────────────────────────────────────────────────

    addFiles(fileList) {
      this.errorEl.hidden = true;
      const free = MAX_FILES - this.entries.length;
      if (free <= 0) {
        this.#error('Du hast schon vier Fotos. Entferne eins, um ein anderes hochzuladen.');
        return;
      }
      const errors = [];
      const valid = [];
      for (const file of Array.from(fileList)) {
        if (!TYPES.includes(file.type)) errors.push(`„${file.name}“: bitte JPG, PNG oder WebP verwenden.`);
        else if (file.size > MAX_SIZE) errors.push(`„${file.name}“ ist zu groß (max. 10 MB).`);
        else valid.push(file);
      }
      if (valid.length > free) errors.push(`Höchstens vier Fotos, ${valid.length - free} habe ich weggelassen.`);
      if (errors.length) this.#error(errors.join(' '));

      const batch = valid.slice(0, free).map((file) => {
        const entry = { id: crypto.randomUUID(), file, state: 'uploading', src: URL.createObjectURL(file) };
        this.#measure(entry);
        this.entries.push(entry);
        return entry;
      });
      if (!batch.length) return;
      this.render();
      this.queue = this.queue.then(() => this.#upload(batch));
    }

    // Sehr kleine Fotos schon im Browser erkennen (ohne KI)
    #measure(entry) {
      const img = new Image();
      img.onload = () => {
        if (Math.min(img.naturalWidth, img.naturalHeight) < SMALL_PX) {
          entry.localHint = 'Das Foto ist sehr klein. Ein größeres Foto bringt mehr Details ins Porträt.';
          this.render();
        }
      };
      img.src = entry.src;
    }

    async #upload(batch) {
      const form = new FormData();
      for (const entry of batch) form.append('photos', entry.file);
      const current = localStorage.getItem(SESSION_KEY);
      if (current) form.append('sessionId', current);

      try {
        const res = await fetch(this.endpoint, { method: 'POST', body: form });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || `Upload fehlgeschlagen (${res.status})`);

        const sessionId = json.sessionId;
        localStorage.setItem(SESSION_KEY, sessionId);
        let data = readData();
        if (!data || data.sessionId !== sessionId) {
          data = { sessionId, originals: [], generated: [], uploadBase: this.uploadBase };
        }
        data.originals = [...(data.originals || []), ...json.files];
        if (!data.uploadBase) data.uploadBase = this.uploadBase;
        // Schon eingetippten Tiernamen gleich mitspeichern – sonst fragt pet-name.js / die PDP erneut
        const typed = this.nameInput.value.trim();
        if (typed && !data.petName) data.petName = window.KunstPfotePetName?.format?.(typed) ?? typed;
        writeData(data);

        json.files.forEach((filename, i) => {
          const entry = batch[i];
          if (!entry) return;
          entry.filename = filename;
          entry.state = 'done';
          delete entry.file;
          delete this.check.photos?.[filename];
        });
        this.check.status = 'pending';

        window.dispatchEvent(new CustomEvent('kunstpfote:photos-uploaded', {
          detail: { sessionId, files: json.files, uploadBase: this.uploadBase },
        }));
        this.render();
        this.#refreshCheck();
      } catch (err) {
        for (const entry of batch) entry.state = 'error';
        this.#error(err.message || 'Upload fehlgeschlagen. Bitte versuch es noch einmal.');
        this.render();
      }
    }

    async removeEntry(id) {
      const entry = this.entries.find((e) => e.id === id);
      if (!entry) return;
      if (entry.state === 'uploading') return;
      if (entry.state === 'done' && entry.filename) {
        const data = readData();
        if (data) {
          try {
            const res = await fetch(`${this.uploadBase}/image/${data.sessionId}/originals/${entry.filename}`, { method: 'DELETE' });
            if (!res.ok && res.status !== 404) throw new Error();
          } catch {
            this.#error('Das Foto konnte nicht entfernt werden. Bitte versuch es gleich noch einmal.');
            return;
          }
          data.originals = (data.originals || []).filter((f) => f !== entry.filename);
          writeData(data);
          window.dispatchEvent(new CustomEvent('kunstpfote:photo-removed', { detail: { filename: entry.filename } }));
        }
      }
      if (entry.src?.startsWith('blob:')) URL.revokeObjectURL(entry.src);
      this.entries = this.entries.filter((e) => e !== entry);
      this.render();
    }

    // ── Prüfergebnis abfragen ────────────────────────────────────────────────

    #refreshCheck() {
      clearTimeout(this.pollTimer);
      this.polls = 0;
      this.#poll();
    }

    async #poll() {
      const sessionId = localStorage.getItem(SESSION_KEY);
      if (!sessionId || !this.dialog.open) return;
      try {
        const res = await fetch(`${this.uploadBase}/check/${sessionId}`, { cache: 'no-store' });
        if (res.status === 404) { this.checkEnabled = false; this.render(); return; }
        const json = await res.json();
        if (json.status === 'disabled') { this.checkEnabled = false; this.render(); return; }
        this.checkEnabled = true;
        this.check = json;
        applyGridFilter(json);
      } catch {
        // Netzfehler: einfach weiter versuchen
      }
      this.render();
      const waiting = this.check.status === 'pending' || this.entries.some((e) => e.state === 'uploading');
      if (waiting && ++this.polls < POLL_MAX && this.dialog.open) {
        this.pollTimer = setTimeout(() => this.#poll(), POLL_MS);
      } else if (this.check.status === 'pending') {
        this.check.status = 'timeout';
        this.render();
      }
    }

    // ── Darstellung ──────────────────────────────────────────────────────────

    #statusOf(entry) {
      if (entry.state !== 'done') return entry.state; // uploading | error
      if (!this.checkEnabled) return 'unchecked';
      const p = this.check.photos?.[entry.filename];
      if (p && p.status && p.status !== 'pending') {
        // Im Browser als sehr klein erkannt → höchstens „eher ungeeignet“
        if (entry.localHint && (p.status === 'good' || p.status === 'ok')) return 'poor';
        // Schwaches Foto, während bessere da sind: geht nicht in die Generierung (Server: used=false)
        if (p.used === false && p.status !== 'rejected') return 'skipped';
        return p.status;
      }
      return this.check.status === 'pending' ? 'pending' : 'unchecked';
    }

    render() {
      const has = this.entries.length > 0;
      this.dialog.classList.toggle('kpu--has-photos', has);
      this.grid.hidden = !has;

      const tiles = this.entries.map((entry) => {
        const status = this.#statusOf(entry);
        const p = this.check.photos?.[entry.filename];
        let hint = '';
        if (['rejected', 'poor', 'ok', 'good'].includes(status)) hint = p?.hint || '';
        if (status === 'skipped') hint = 'Deine anderen Fotos sind besser, dieses lasse ich beim Porträt weg.';
        // Auch geeignete Fotos bekommen eine positive Rückmeldung
        if (!hint && (status === 'good' || status === 'ok')) hint = 'Passt gut fürs Porträt.';
        if (!hint && entry.localHint && status !== 'rejected') hint = entry.localHint;
        const text = STATE_TEXT[status] || '';
        return `<li class="kpu-tile" data-state="${entry.state}" data-status="${status}">
          <div class="kpu-tile__pic">
            <img src="${esc(entry.src)}" alt="" loading="lazy">
            ${entry.state !== 'uploading' ? `<button type="button" class="kpu-tile__del" data-kpu-del="${entry.id}" aria-label="Foto entfernen">${ICON_X}</button>` : ''}
          </div>
          <span class="kpu-tile__state">${text}</span>
          <p class="kpu-tile__hint">${esc(hint)}</p>
        </li>`;
      });
      if (has && this.entries.length < MAX_FILES) {
        tiles.push(`<li class="kpu-tile"><button type="button" class="kpu-add" data-kpu-add-tile data-clarity="Upload-Modal Foto hinzufügen">${ICON_PLUS}<span>Foto hinzufügen</span></button></li>`);
      }
      this.grid.innerHTML = tiles.join('');

      this.#renderSummary();
      this.#renderFoot();
    }

    #overall() {
      const statuses = this.entries.map((e) => this.#statusOf(e));
      if (!statuses.length) return 'empty';
      if (statuses.includes('uploading')) return 'uploading';
      const done = statuses.filter((s) => s !== 'error');
      if (!done.length) return 'empty';
      if (!this.checkEnabled || done.every((s) => s === 'unchecked')) {
        return done.length >= MIN_GOOD ? 'unchecked' : 'need-more';
      }
      if (done.includes('pending')) return 'pending';
      const usable = done.filter((s) => s !== 'rejected' && s !== 'skipped');
      if (!usable.length) return 'rejected';
      const good = done.filter((s) => s === 'good' || s === 'ok').length;
      if (!good) return 'warn';
      if (good < MIN_GOOD) return 'need-more';
      return done.length > usable.length ? 'good-partial' : 'good';
    }

    #renderSummary() {
      const state = this.#overall();
      const map = {
        uploading: ['', 'Deine Fotos werden hochgeladen …', true],
        pending: ['', 'Ich schaue mir deine Fotos kurz an …', true],
        good: ['ok', '<strong>Deine Fotos passen.</strong>Damit kann ich gut arbeiten.'],
        'good-partial': ['ok', '<strong>Passt.</strong>Fotos, die sich nicht eignen, lasse ich beim Porträt weg.'],
        warn: ['warn', '<strong>Damit wird das Porträt deinem Tier nicht ähnlich genug.</strong>Lade bitte zwei scharfe Fotos bei Tageslicht hoch, auf denen das Gesicht gut zu sehen ist.'],
        'need-more': ['warn', '<strong>Noch ein Foto, dann geht’s los.</strong>Mit mindestens zwei guten Fotos, gern aus verschiedenen Blickwinkeln, wird dein Tier deutlich ähnlicher.'],
        rejected: ['bad', '<strong>Mit diesen Fotos kann ich kein Porträt erstellen.</strong>Bitte lade ein echtes Foto hoch, auf dem nur dein Tier zu sehen ist.'],
      };
      // Tageslimit offen kommunizieren (Server: MAX_GENERATIONS pro Session und Tag)
      const gen = this.check.generations;
      if (gen && ['good', 'good-partial', 'unchecked'].includes(state) && map[state]) {
        map[state][1] += gen.left > 0
          ? `<br><span class="kpu-note">Pro Tag kannst du ${gen.limit} Porträt-Vorschauen erstellen, heute noch ${gen.left}.</span>`
          : `<br><span class="kpu-note">Heute hast du schon ${gen.limit} Vorschauen erstellt. Morgen kannst du wieder neue ausprobieren.</span>`;
      }
      if (this.check.status === 'timeout' && state === 'pending') {
        map.pending = ['', 'Die Prüfung dauert gerade länger. Du kannst trotzdem weitermachen.', false];
      }
      // Prüfung fehlgeschlagen (n8n/Gemini nicht erreichbar): Kunde bekommt trotzdem eine Rückmeldung
      if (state === 'unchecked' && this.checkEnabled && this.check.status === 'error') {
        map.unchecked = ['warn', '<strong>Ich konnte deine Fotos gerade nicht prüfen.</strong>Du kannst trotzdem weitermachen. Am besten klappt es mit einem scharfen Foto bei Tageslicht, auf dem nur dein Tier zu sehen ist.'];
      }
      const entry = map[state];
      this.summary.hidden = !entry;
      if (!entry) return;
      const [tone, html, spin] = entry;
      this.summary.dataset.tone = tone;
      this.summary.innerHTML = `${spin ? '<span class="kpu__spinner" aria-hidden="true"></span>' : ''}<div>${html}</div>`;
    }

    #context() {
      const src = this.source;
      const pdp = src?.closest?.('personalization-form-component');
      if (pdp) {
        const btn = pdp.querySelector('.pf-generate-btn');
        return { label: 'Vorschau erstellen', run: () => btn?.click() };
      }
      const home = src?.closest?.('upload-steps-section, photo-upload-block');
      if (home) {
        const section = src.closest('upload-steps-section') || home;
        const gen = section.querySelector('.photo-upload__generate-btn');
        if (gen && !gen.hidden) return { label: gen.textContent.trim() || 'Porträt erstellen', run: () => gen.click() };
        const link = section.querySelector('[data-step="2"] .us-card__link');
        if (link) return { label: 'Welt aussuchen', run: () => { window.location.href = link.href; } };
      }
      return { label: 'Fertig', run: null };
    }

    #renderFoot() {
      const state = this.#overall();
      const ctx = this.#context();
      // Weiter erst mit mindestens einem geeigneten Foto (gut/ok). Ausnahme: Prüfung nicht verfügbar
      // oder hängt (unchecked/timeout) – dann nicht blockieren, sonst kauft niemand mehr.
      const timedOut = state === 'pending' && this.check.status === 'timeout';
      const usableCount = this.entries.filter((e) => e.state === 'done' && !['rejected', 'skipped'].includes(this.#statusOf(e))).length;
      const ready = ['good', 'good-partial', 'unchecked'].includes(state) || (timedOut && usableCount >= MIN_GOOD);
      this.primary.textContent = state === 'pending' && !timedOut ? 'Fotos werden geprüft …' : ctx.label;
      const limitHit = this.check.generations?.left === 0;
      if (limitHit && ready) this.primary.textContent = 'Heute alle Vorschauen genutzt';
      this.primary.disabled = !ready || limitHit;
      this.addBtn.hidden = !['warn', 'rejected', 'need-more'].includes(state) || this.entries.length >= MAX_FILES;
      this.addBtn.textContent = state === 'need-more' ? 'Weiteres Foto hochladen' : 'Anderes Foto hochladen';
    }

    #onPrimary() {
      if (this.primary.disabled) return;
      const ctx = this.#context();
      this.#saveName(); // vor dem Start, damit der Name schon bei /generate (und in Telegram) ankommt
      this.close();
      // Erst nach dem Schließen auslösen, damit das Generierungs-Modal nicht unter diesem liegt
      if (ctx.run) setTimeout(ctx.run, 60);
    }

    #error(msg) {
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }
  }

  customElements.define('kp-upload-modal', KpUploadModal);

  // ── Nicht verwendete Fotos in den Foto-Rastern von PDP und Startseite ausblenden ──
  // (Fotos bleiben auf dem Server und in „Meine Fotos“ sichtbar, dort mit Status.)
  const GRID_ITEMS = '.pf-preview-grid .pf-preview-item, .photo-upload__preview-grid .photo-upload__preview-item';

  function applyGridFilter(check) {
    const unused = new Set(
      Object.entries(check?.photos || {})
        .filter(([, p]) => p && (p.used ?? p.use) === false)
        .map(([f]) => f),
    );
    document.querySelectorAll(GRID_ITEMS).forEach((li) => {
      const src = li.querySelector('img')?.getAttribute('src') || '';
      const file = src.includes('/originals/') ? src.split('/originals/')[1].split('?')[0] : '';
      li.classList.toggle('kp-photo-unused', Boolean(file) && unused.has(file));
    });
  }

  let filterTimer = null;
  function refreshGridFilter(delay = 80) {
    clearTimeout(filterTimer);
    filterTimer = setTimeout(async () => {
      const data = readData();
      if (!data?.sessionId || !document.querySelector(GRID_ITEMS)) return;
      const base = (data.uploadBase || 'https://upload.kunstpfote.de').replace(/\/$/, '');
      try {
        const res = await fetch(`${base}/check/${data.sessionId}`, { cache: 'no-store' });
        if (!res.ok) return;
        const check = await res.json();
        applyGridFilter(check);
        if (check.status === 'pending') refreshGridFilter(2000);
      } catch {}
    }, delay);
  }

  window.addEventListener('kunstpfote:photos-uploaded', () => refreshGridFilter());
  window.addEventListener('kunstpfote:photo-removed', () => refreshGridFilter());
  if (document.readyState === 'complete') refreshGridFilter(300);
  else window.addEventListener('load', () => refreshGridFilter(300));

  window.KunstPfoteUpload = {
    open: (opts) => document.querySelector('kp-upload-modal')?.open(opts),
    close: () => document.querySelector('kp-upload-modal')?.close(),
    isOpen: () => Boolean(document.querySelector('kp-upload-modal')?.isOpen()),
  };
})();
